import { useMemo, useRef } from 'react';
import type { InvoiceListItem } from '@ip/shared-types';

/**
 * Selection spans pages (plain ids), but the list state only holds the
 * CURRENT page's rows. A row can only be (de)selected while it is visible,
 * so every selected row has been rendered at least once — cache each page's
 * rows by id and resolve the selected ids against that cache. This yields
 * full row data (amounts, currency, PLN conversion) for cross-page
 * selections, e.g. the footer sum of selected invoices.
 */
export function useSelectedInvoiceItems(
  invoices: InvoiceListItem[],
  selectedIds: string[],
): InvoiceListItem[] {
  const cacheRef = useRef(new Map<string, InvoiceListItem>());
  return useMemo(() => {
    for (const inv of invoices) cacheRef.current.set(inv.id, inv);
    const items: InvoiceListItem[] = [];
    for (const id of selectedIds) {
      const inv = cacheRef.current.get(id);
      if (inv) items.push(inv);
    }
    return items;
  }, [invoices, selectedIds]);
}
